/**
 * Automatikus szünet — a beállítás és a visszajelzés (GRUNDO #72).
 *
 * A logika a `tracking/autoPause.ts`-ben van (tiszta függvények); itt csak az
 * eszközhöz kötött kapcsoló és a hang + rezgés.
 *
 * MIÉRT `localStorage`? Ugyanazért, mint a befejezés-gesztusnál és a
 * `feedbackSettings`-nél: eszközhöz tartozik, nem fiókhoz.
 */

import { Capacitor, registerPlugin } from '@capacitor/core';
import { useSyncExternalStore } from 'react';
import { feedbackSettings } from './feedbackSettings';
import { playSound } from './sound';

const STORAGE_KEY = 'grundo.autoPause';

const listeners = new Set<() => void>();

/** Alapból BE (Geri döntése). Csak a kifejezett `off` kapcsolja ki. */
export function autoPauseEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
}

export function setAutoPauseEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? 'on' : 'off');
  } catch {
    /* A tároló nem elérhető — a kapcsoló a munkamenet végéig sem marad meg. */
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useAutoPauseEnabled(): boolean {
  return useSyncExternalStore(subscribe, autoPauseEnabled, () => true);
}

interface HapticsPlugin {
  pulse(options: { double: boolean }): Promise<void>;
}
const nativeHaptics = registerPlugin<HapticsPlugin>('RefhexHaptics');

/**
 * Hang és rezgés az automatikus szünetnél és folytatásnál (Geri: mindkettő).
 * A hang a meglévő szünet/folytatás hang, de SAJÁT kapcsolóval (Beállítások →
 * Hangok és rezgések → „Automatikus szünet hangja"); a főkapcsoló és a
 * hangerő rá is vonatkozik. A rezgés a „Rezgések" kapcsolót követi; szünetnél
 * egy, folytatásnál kettő.
 */
export function announceAutoPause(event: 'pause' | 'resume'): void {
  const settings = feedbackSettings();
  if (settings.soundAutoPause) {
    // A `soundActivity` (kézi szünet, kör, befejezés) ettől független.
    playSound(event === 'pause' ? 'pause-activity' : 'resume-activity', {
      ...settings,
      soundActivity: true,
    });
  }
  if (!settings.vibrationEnabled) return;
  try {
    if (Capacitor.isNativePlatform()) {
      if (Capacitor.isPluginAvailable('RefhexHaptics')) {
        void nativeHaptics.pulse({ double: event === 'resume' }).catch(() => undefined);
      }
    } else if (typeof navigator.vibrate === 'function') {
      navigator.vibrate(event === 'pause' ? 60 : [40, 60, 40]);
    }
  } catch {
    /* A rezgés opcionális — hiánya sosem zavarhatja a mérést. */
  }
}
